<script setup lang="ts">
import { DEFAULT_LABELS, type CalendarLabels, type VenueFilter } from './types';

const { labels = DEFAULT_LABELS } = defineProps<{ labels?: CalendarLabels }>();
const model = defineModel<VenueFilter>({ default: 'both' });

const options = [
  { value: 'both' as const, key: 'both', label: () => labels.filterBoth },
  { value: 'lasalle' as const, key: 'lasalle', label: () => labels.filterLasalle },
  { value: 'serre' as const, key: 'serre', label: () => labels.filterSerre },
];
</script>

<template>
  <fieldset class="venue-filter" role="radiogroup" :aria-label="labels.filterLabel" data-testid="venue-filter">
    <legend class="vf-legend">{{ labels.filterLabel }}</legend>
    <label
      v-for="opt in options"
      :key="opt.key"
      class="vf-option"
      :class="{ 'vf-option--checked': model === opt.value }"
      :data-testid="`venue-filter-${opt.key}`"
    >
      <input v-model="model" type="radio" name="venue-filter" :value="opt.value" class="vf-input" />
      <span v-if="opt.key !== 'both'" class="vf-swatch" :class="`vf-swatch--${opt.key}`" aria-hidden="true" />
      <span class="vf-text">{{ opt.label() }}</span>
    </label>
  </fieldset>
</template>

<style scoped>
.venue-filter {
  --venue-lasalle: #8b5a3c;
  --venue-serre: #6b8f5e;
  display: flex;
  gap: 0;
  margin: 0 0 12px;
  padding: 0;
  border: 1px solid #cfd6e5;
  border-radius: 8px;
  overflow: hidden;
}
.vf-legend {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}
.vf-option {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1;
  min-height: 44px;
  cursor: pointer;
  background: #fff;
  border-right: 1px solid #cfd6e5;
}
.vf-option:last-child { border-right: none; }
/* Selected state: fill + weight, never color alone (spec §5). */
.vf-option--checked {
  background: #f0ece6;
  font-weight: 700;
  box-shadow: inset 0 -3px 0 #1d1d1d;
}
.vf-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}
.vf-option:has(.vf-input:focus-visible) { outline: 2px solid #1d1d1d; outline-offset: -2px; }
.vf-swatch { width: 12px; height: 12px; border-radius: 3px; }
.vf-swatch--lasalle { background-color: var(--venue-lasalle); }
.vf-swatch--serre { background-color: var(--venue-serre); }
@media (forced-colors: active) {
  .vf-option--checked { box-shadow: none; outline: 2px solid Highlight; outline-offset: -2px; }
  .vf-swatch { border: 1px solid CanvasText; }
}
@media (max-width: 576px) {
  .venue-filter { position: sticky; top: var(--eauget-sticky-top, 0); z-index: 2; }
}
</style>
